import Vue from 'vue';
import { cloneDeep, isEqual, debounce } from 'lodash';
import DownloadExportButton from 'components/project/analysis/results/widgets/DownloadExportButton.vue';
import WidgetMenu from 'components/DataWidgets/WidgetMenu/WidgetMenu.vue';
import WidgetFrame from 'components/widgets/WidgetFrame/WidgetFrame.vue';
import { fetch_correlation_data } from 'src/store/modules/data/api';
import icon from 'assets/img/dashboards/dash-correlation.svg';
import errorIcon from 'assets/icons/alert-bubble.svg';
import UpDown from 'components/widgets/UpDown/UpDown.vue';
import TableRow from './TableRow.vue';
import FloatingPanel from 'components/widgets/FloatingPanel/FloatingPanel.vue';
import DataToolTip from "components/DataWidgets/DataToolTip/DataToolTip.vue";
import WidgetMessagePanel from 'components/widgets/WidgetMessagePanel/WidgetMessagePanel.vue';
const roundValue = (num) => {
    return Math.round(num * 100) / 100;
};
const makeDatum = (data) => {
    const a = data[0];
    const b = data[1];
    const freq = data[2];
    const pmi = roundValue(data[3]);
    return {
        a_label: a[1],
        b_label: b[1],
        a_name: a[2],
        b_name: b[2],
        a_type: a[0],
        b_type: b[0],
        a_freq: freq[0],
        b_freq: freq[1],
        pair_freq: freq[2],
        pmi,
    };
};
export const getNPMILabel = (npmi) => {
    if (npmi >= 0.5)
        return 'High positive correlation';
    if (npmi >= 0.25)
        return 'Positive correlation';
    if (npmi > -0.25)
        return 'Little or no correlation';
    if (npmi > -0.5)
        return 'Negative correlation';
    return 'High negative correlation';
};
export const getNPMIColour = (npmi) => {
    if (npmi >= 0.5)
        return '#21ba45';
    if (npmi >= 0.25)
        return '#11acdf';
    if (npmi > -0.25)
        return '#7F7F7F';
    if (npmi > -0.5)
        return '#f89516';
    return '#ee3824';
};
const defaultPairTypes = [
    'theme/categorical',
    'theme/numeric',
];
export default Vue.extend({
    components: {
        DownloadExportButton,
        WidgetFrame,
        WidgetMenu,
        TableRow,
        UpDown,
        FloatingPanel,
        DataToolTip,
        WidgetMessagePanel,
    },
    props: {
        data: { type: Object, required: false, default: null },
        exportName: { type: String, required: false, default: '' },
        devMode: { type: Boolean, required: false, default: false },
        zoomToRoute: { type: Object, required: false, default: null },
        isZoomed: { type: Boolean, required: false, default: false },
        banner: { type: Object, default: () => null, required: false },
        masked: { type: Boolean, required: false, default: false },
        queries: { type: Array, required: false, default: () => [] },
        schema: { type: Array, required: true },
        config: { type: Object, required: false, default: null },
        goToPivot: { type: Function, required: false, default: () => { } },
        documentCount: { type: Number, required: false, default: null },
        totalDocsCount: { type: Number, required: false, default: null },
        documentQuery: { type: Number, required: false, default: null },
        /** dashboardId (for analytics) */
        dashboardId: { type: Number, required: true },
        toQueryRoute: { type: Object, required: false, default: null },
        groupbyNotSupported: { type: Boolean, default: false },
    },
    data() {
        return {
            icon,
            errorIcon,
            hasErrored: false,
            width: 0,
            height: 0,
            fieldOptions: [],
            fieldSelection: [],
            minPairFreq: 50,
            minCorrelation: -0.99,
            maxCorrelation: 0.99,
            pairTypes: defaultPairTypes,
            thresholdFilters: ['dependent'],
            sort: { field: 'pmi', asc: false, absolute: true },
            maxFields: 80,
            clickedRow: null,
            toolTipRow: null,
            mouseX: 0,
            mouseY: 0,
            nCondensedRows: 6,
            nExpandedRows: 100,
        };
    },
    computed: {
        toolTipContent() {
            if (!this.toolTipRow)
                return null;
            return {
                title: null,
                action: 'Click for explore options',
                data: [
                    {
                        label: 'Frequency of A (#/%):',
                        value: {
                            text: `${this.toolTipRow.a_freq} / ${roundValue(this.toolTipRow.a_freq / this.documentCount * 100)}%`,
                            style: { 'font-weight': 'bold' }
                        }
                    },
                    {
                        label: 'Frequency of B (#/%):',
                        value: {
                            text: `${this.toolTipRow.b_freq} / ${roundValue(this.toolTipRow.b_freq / this.documentCount * 100)}%`,
                            style: { 'font-weight': 'bold' }
                        }
                    },
                    {
                        label: 'Intersection frequency of A,B:',
                        value: {
                            text: `${this.toolTipRow.pair_freq}`,
                            style: { 'font-weight': 'bold' }
                        }
                    },
                    {
                        label: 'Percentage of intersection in A:',
                        value: {
                            text: `${roundValue(this.toolTipRow.pair_freq / this.toolTipRow.a_freq * 100)}%`,
                            style: { 'font-weight': 'bold' }
                        }
                    },
                    {
                        label: 'Percentage of intersection in B:',
                        value: {
                            text: `${roundValue(this.toolTipRow.pair_freq / this.toolTipRow.b_freq * 100)}%`,
                            style: { 'font-weight': 'bold' }
                        }
                    },
                    {
                        label: 'NPMI value:',
                        value: {
                            text: `${this.toolTipRow.pmi}`,
                            style: { 'font-weight': 'bold' }
                        }
                    },
                    {
                        label: 'Correlation:',
                        value: {
                            text: `${getNPMILabel(this.toolTipRow.pmi)}`,
                            style: { 'font-weight': 'bold', 'color': getNPMIColour(this.toolTipRow.pmi) }
                        }
                    },
                ]
            };
        },
        hasValidData() {
            return this.dataRows.length > 0;
        },
        dataRows() {
            const results = cloneDeep(this.data?.results ?? []);
            const data = results.map(makeDatum);
            const limit = this.isZoomed ? this.nExpandedRows : this.nCondensedRows;
            return data.slice(0, limit);
        },
        showFooter() {
            const limit = this.isZoomed ? this.nExpandedRows : this.nCondensedRows;
            return (this.data?.results ?? []).length > limit - 1;
        },
        minPairFreqofTotal() {
            if (!this.documentQuery)
                return 30;
            return Math.round(this.documentQuery * 0.005);
        },
        menus() {
            return [
                {
                    name: 'Fields',
                    selection: `${this.fieldSelection.length} selected`,
                    options: [[{
                                type: 'checkbox',
                                options: this.fieldOptions,
                                showSelected: true,
                                selected: this.fieldSelection
                            }]]
                },
                {
                    name: 'Min Pair Freq (#)',
                    selection: this.minPairFreq,
                    options: [[{
                                type: 'inputNumber',
                                // Without having something in the options list,
                                // the dropdown shows "No Data"
                                options: ['inputNumber'],
                                settings: {
                                    min: 0,
                                    step: 10,
                                },
                                showSelected: true,
                                selected: this.minPairFreq
                            }]]
                },
                {
                    name: 'Pair Types',
                    selection: `${this.pairTypes.length} selected`,
                    options: [[{
                                type: 'checkbox',
                                options: [
                                    'theme/theme',
                                    'theme/categorical',
                                    'theme/numeric',
                                    'categorical/categorical',
                                    'categorical/numeric',
                                    'numeric/numeric',
                                ],
                                showSelected: true,
                                selected: this.pairTypes
                            }]]
                },
                {
                    name: 'Misc. Filters',
                    selection: `${this.minCorrelation} – ${this.maxCorrelation}`,
                    options: [
                        [{
                                title: 'Minimum Correlation',
                                type: 'inputNumber',
                                // Without having something in the options list,
                                // the dropdown shows "No Data"
                                options: ['inputNumber'],
                                settings: {
                                    min: -1,
                                    step: 0.1,
                                    max: 0.99
                                },
                                showSelected: true,
                                selected: this.minCorrelation
                            }, {
                                title: 'Maximum Correlation',
                                type: 'inputNumber',
                                // Without having something in the options list,
                                // the dropdown shows "No Data"
                                options: ['inputNumber'],
                                settings: {
                                    min: -0.99,
                                    step: 0.1,
                                    max: 1
                                },
                                showSelected: true,
                                selected: this.maxCorrelation
                            }, {
                                title: 'Other Filters',
                                type: 'checkbox',
                                options: [
                                    { label: 'Coverage < 100%', value: 'dependent' },
                                ],
                                showSelected: true,
                                selected: this.thresholdFilters
                            }]
                    ]
                },
            ];
        },
        tooManyFields() {
            return this.fieldSelection.length > this.maxFields;
        },
        dataSetTooLarge() {
            return (this.totalDocsCount > 1000000) ||
                (this.totalDocsCount > 400000 && this.fieldSelection.length > 20);
        },
        isLoading() {
            return this.data === null && !this.hasValidData && this.totalDocsCount === null;
        },
    },
    watch: {
        config: {
            deep: true,
            handler() {
                this.setOptionsFromConfig();
            }
        },
        schema: {
            deep: true,
            immediate: true,
            handler(newVal, oldVal) {
                if (!isEqual(oldVal, newVal)) {
                    this.updateFieldOptions();
                }
            }
        },
        data: {
            deep: true,
            handler(newVal, oldVal) {
                if (!isEqual(oldVal, newVal)) {
                    this.clickedRow = null;
                }
            }
        },
        queries: {
            deep: true,
            handler() {
                this.fetchData();
            }
        },
        sort: {
            deep: true,
            handler(newVal, oldVal) {
                if (!isEqual(oldVal, newVal)) {
                    this.fetchData();
                }
            }
        },
        fieldSelection() {
            this.fetchData();
        },
        minPairFreq: debounce(function () { this.fetchData(); }, 500),
        pairTypes: debounce(function () { this.fetchData(); }, 500),
        thresholdFilters: debounce(function () { this.fetchData(); }, 500),
        minCorrelation: debounce(function () { this.fetchData(); }, 500),
        maxCorrelation: debounce(function () { this.fetchData(); }, 500),
    },
    mounted() {
        this.setOptionsFromConfig();
        this.fetchData();
    },
    created() {
        if (typeof window !== 'undefined') {
            document.addEventListener('click', this.clickedOutside);
        }
    },
    beforeDestroy() {
        if (typeof window !== 'undefined') {
            document.removeEventListener('click', this.clickedOutside);
        }
    },
    methods: {
        clickedOutside(e) {
            if (this.clickedRow === null)
                return;
            const contained = this.$refs.wrapper.contains(e.target);
            if (!contained) {
                this.clickedRow = null;
            }
        },
        getChartEl() {
            return this.$el.querySelector('svg');
        },
        updateConfig() {
            const updated = Object.assign({}, this.config, {
                options: {
                    fieldSelection: this.fieldSelection,
                    minPairFreq: this.minPairFreq,
                    pairTypes: this.pairTypes,
                    thresholdFilters: this.thresholdFilters,
                    minCorrelation: this.minCorrelation,
                    maxCorrelation: this.maxCorrelation
                }
            });
            this.$emit('config-changed', updated);
        },
        setOptionsFromConfig() {
            this.fieldSelection = this.config?.options?.fieldSelection ?? this.fieldOptions;
            this.minPairFreq = this.config?.options?.minPairFreq ?? this.minPairFreqofTotal;
            this.pairTypes = this.config?.options?.pairTypes ?? defaultPairTypes;
            this.thresholdFilters = this.config?.options?.thresholdFilters ?? ['dependent'];
            this.minCorrelation = this.config?.options?.minCorrelation ?? -0.99;
            this.maxCorrelation = this.config?.options?.maxCorrelation ?? 0.99;
        },
        setChartDimensions(width, height) {
            this.width = width;
            this.height = height;
        },
        async fetchData(force = false) {
            this.hasErrored = false;
            if (this.tooManyFields)
                return;
            if (this.dataSetTooLarge)
                return;
            try {
                this.$emit('requires', 'segment-correlation', {
                    'fields': this.fieldSelection,
                    'queries': this.queries
                        .map((q) => ({
                        name: q.name,
                        value: q.query_value
                    })),
                    'min_pair_frequency': this.minPairFreq,
                    'pair_combinations': this.pairTypes,
                    'threshold_filters': this.thresholdFilters,
                    'min_correlation': this.minCorrelation,
                    'max_correlation': this.maxCorrelation,
                    'sort': this.sort,
                    'limit': this.isZoomed ? this.nExpandedRows : this.nCollapsedRows,
                }, force, true, fetch_correlation_data);
            }
            catch (e) {
                this.hasErrored = true;
            }
        },
        setMenuSelection(name, [title, val]) {
            if (name === 'Fields') {
                this.fieldSelection = val;
                this.$analytics.track.correlationsWidget.changeFields(name, val);
            }
            if (name === 'Min Pair Freq (#)') {
                this.minPairFreq = val;
                this.$analytics.track.correlationsWidget.changeMinPairFreq(name, val);
            }
            if (name === 'Pair Types') {
                this.pairTypes = val;
                this.$analytics.track.correlationsWidget.changePairTypes(name, val);
            }
            if (name === 'Misc. Filters') {
                if (title === 'Minimum Correlation') {
                    this.minCorrelation = val;
                }
                if (title === 'Maximum Correlation') {
                    this.maxCorrelation = val;
                }
                if (title === 'Other Filters') {
                    this.thresholdFilters = val;
                }
                // this.$analytics.track.correlationsWidget.changeThresholds(name, val)
                // TODO
            }
            this.updateConfig();
        },
        rowClick(index) {
            if (index === this.clickedRow) {
                this.clickedRow = null;
            }
            else {
                this.clickedRow = index;
            }
        },
        closeRowMenu() {
            this.clickedRow = null;
        },
        updateFieldOptions() {
            this.fieldOptions = this.schema
                .filter((row) => !['TEXT', 'DATE', 'DATE_TIME'].includes(row.typename))
                .map((row) => row.name);
        },
        sortFreqClick() {
            this.sort = {
                field: 'freq',
                asc: this.sort?.field === 'freq'
                    ? !this.sort.asc
                    : false,
                absolute: null
            };
            this.$analytics.track.correlationsWidget.changeSort('freq', this.sort.asc, this.sort.absolute);
        },
        sortPmiClick() {
            if (this.sort?.field !== 'pmi') {
                this.sort = {
                    field: 'pmi',
                    asc: false,
                    absolute: true,
                };
            }
            else {
                let asc;
                let absolute;
                if (this.sort.absolute) {
                    // Absolute -> Positive
                    asc = false;
                    absolute = false;
                }
                else if (!this.sort.asc) {
                    // Positive -> Negative
                    asc = true;
                    absolute = false;
                }
                else if (this.sort.asc) {
                    // Negative -> Absolute
                    asc = false;
                    absolute = true;
                }
                this.sort = {
                    field: 'pmi',
                    asc,
                    absolute,
                };
            }
            this.$analytics.track.correlationsWidget.changeSort('pmi', this.sort.asc, this.sort.absolute);
        },
        excludeField(field) {
            this.fieldSelection =
                this.fieldSelection.filter((f) => f !== field);
            this.$analytics.track.correlationsWidget.excludeFromActionMenu(field);
            this.updateConfig();
            this.closeRowMenu();
        },
        toggleFilters(segments) {
            for (const [field, segment] of segments) {
                this.$emit('toggle-filter', field, segment);
            }
            this.$analytics.track.correlationsWidget.filterFromActionMenu(segments);
            this.closeRowMenu();
        },
        openPivotTable(col, row) {
            if (col === 'Theme')
                col = 'Themes';
            if (row === 'Theme')
                row = 'Themes';
            this.$analytics.track.correlationsWidget.openPivotTableFromActionMenu(col, row);
            this.closeRowMenu();
            this.goToPivot(col, row);
        },
        updateMousePosition(event) {
            this.mouseX = event.clientX;
            this.mouseY = event.clientY;
        },
        drilldown(name) {
            const query = this.queries.find((q) => q.name === name);
            this.$router.push({
                ...this.toQueryRoute,
                params: {
                    ...this.toQueryRoute.params,
                    queryId: query.id
                },
                query: {
                    filters: this.toQueryRoute.query.filters
                }
            });
            this.$emit('scroll-to-top');
            this.$analytics.track.correlationsWidget.drilldownTheme(name);
        },
        getCsvData() {
            this.$analytics.track.correlationsWidget.exportCSV();
            return this.dataRows.map((row) => ({
                'A': `${row.a_label}: ${row.a_name}`,
                'B': `${row.b_label}: ${row.b_name}`,
                'A Frequency': row.a_freq,
                'B Frequency': row.b_freq,
                'Pair Frequency': row.pair_freq,
                'NPMI': row.pmi,
            }));
        },
        // error panel items
        refresh() {
            window.location.reload();
        },
        reload() {
            this.fetchData(true);
        },
        contact() {
            try {
                window.Intercom('show');
            }
            catch (e) {
                console.warn('intercom show failed');
            }
        },
    }
});
